import api from "./api";
import { toast } from "react-toastify";
import { i18n } from "../translate/i18n";

const logisticsService = {
  /**
   * Atualizar código de rastreamento de um pedido
   */
  async updateTracking(orderId, trackingData) {
    try {
      const response = await api.put(`/orders/${orderId}/tracking`, trackingData);

      if (response.data.success) {
        toast.success(i18n.t("logistics.tracking.updateSuccess"));
      }

      return response.data;
    } catch (error) {
      console.error("Erro ao atualizar rastreamento:", error);

      const message = error.response?.data?.message ||
        i18n.t("logistics.tracking.updateError");

      toast.error(message);
      throw error;
    }
  },

  /**
   * Consultar informações de rastreamento de um pedido
   */
  async getTracking(orderId) {
    try {
      const response = await api.get(`/orders/${orderId}/tracking`);
      return response.data.data;
    } catch (error) {
      console.error("Erro ao consultar rastreamento:", error);

      if (error.response?.status !== 404) {
        const message = error.response?.data?.message ||
          i18n.t("logistics.tracking.fetchError");

        toast.error(message);
      }

      throw error;
    }
  },

  /**
   * Atualizar status de entrega de um pedido
   */
  async updateDelivery(orderId, deliveryData) {
    try {
      const response = await api.put(`/orders/${orderId}/delivery`, deliveryData);

      if (response.data.success) {
        toast.success(i18n.t("logistics.delivery.updateSuccess"));
      }

      return response.data;
    } catch (error) {
      console.error("Erro ao atualizar entrega:", error);

      const message = error.response?.data?.message ||
        i18n.t("logistics.delivery.updateError");

      toast.error(message);
      throw error;
    }
  },

  /**
   * Sincronizar rastreamento externo
   */
  async syncExternalTracking(orderId, provider) {
    try {
      const response = await api.post(`/orders/${orderId}/sync-tracking`, { provider });

      if (response.data.success) {
        toast.success(i18n.t("logistics.sync.success"));
      }

      return response.data;
    } catch (error) {
      console.error("Erro na sincronização externa:", error);

      const message = error.response?.data?.message ||
        i18n.t("logistics.sync.error");

      toast.error(message);
      throw error;
    }
  },

  /**
   * Obter estatísticas de logística
   */
  async getLogisticsStats(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await api.get(`/orders/logistics/stats?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error("Erro ao obter estatísticas de logística:", error);

      const message = error.response?.data?.message ||
        i18n.t("logistics.stats.fetchError");

      toast.error(message);
      throw error;
    }
  },

  /**
   * Formatar data de evento de rastreamento
   */
  formatEventDate(dateString) {
    if (!dateString) return '';

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    }).format(new Date(dateString));
  },

  /**
   * Obter ícone para evento de rastreamento
   */
  getEventIcon(eventType) {
    const iconMap = {
      'ORDER_CREATED': '📝',
      'PAYMENT_CONFIRMED': '✅',
      'IN_PREPARATION': '👨‍🍳',
      'READY_FOR_PICKUP': '📦',
      'SHIPPED': '🚚',
      'OUT_FOR_DELIVERY': '🏃‍♂️',
      'DELIVERED': '✅',
      'DELIVERY_FAILED': '❌',
      'RETURNED': '↩️',
      'CANCELLED': '🚫',
      'CUSTOMS_CLEARANCE': '🛃',
      'SORTING_CENTER': '📋',
      'TRANSFER': '🔄',
      'VEHICLE_LOADED': '🚛',
      'DELIVERY_ATTEMPTED': '🔄',
    };

    return iconMap[eventType] || '📍';
  },

  /**
   * Obter cor para status de rastreamento
   */
  getStatusColor(eventType) {
    const colorMap = {
      'ORDER_CREATED': '#6c757d',
      'PAYMENT_CONFIRMED': '#28a745',
      'IN_PREPARATION': '#fd7e14',
      'READY_FOR_PICKUP': '#20c997',
      'SHIPPED': '#007bff',
      'OUT_FOR_DELIVERY': '#6f42c1',
      'DELIVERED': '#28a745',
      'DELIVERY_FAILED': '#dc3545',
      'RETURNED': '#dc3545',
      'CANCELLED': '#dc3545',
      'CUSTOMS_CLEARANCE': '#17a2b8',
      'SORTING_CENTER': '#ffc107',
      'TRANSFER': '#6c757d',
      'VEHICLE_LOADED': '#6f42c1',
      'DELIVERY_ATTEMPTED': '#ffc107',
    };

    return colorMap[eventType] || '#6c757d';
  },

  /**
   * Traduzir tipo de evento
   */
  translateEventType(eventType) {
    const translations = {
      'ORDER_CREATED': i18n.t("logistics.events.ORDER_CREATED"),
      'PAYMENT_CONFIRMED': i18n.t("logistics.events.PAYMENT_CONFIRMED"),
      'IN_PREPARATION': i18n.t("logistics.events.IN_PREPARATION"),
      'READY_FOR_PICKUP': i18n.t("logistics.events.READY_FOR_PICKUP"),
      'SHIPPED': i18n.t("logistics.events.SHIPPED"),
      'OUT_FOR_DELIVERY': i18n.t("logistics.events.OUT_FOR_DELIVERY"),
      'DELIVERED': i18n.t("logistics.events.DELIVERED"),
      'DELIVERY_FAILED': i18n.t("logistics.events.DELIVERY_FAILED"),
      'RETURNED': i18n.t("logistics.events.RETURNED"),
      'CANCELLED': i18n.t("logistics.events.CANCELLED"),
      'CUSTOMS_CLEARANCE': i18n.t("logistics.events.CUSTOMS_CLEARANCE"),
      'SORTING_CENTER': i18n.t("logistics.events.SORTING_CENTER"),
      'TRANSFER': i18n.t("logistics.events.TRANSFER"),
      'VEHICLE_LOADED': i18n.t("logistics.events.VEHICLE_LOADED"),
      'DELIVERY_ATTEMPTED': i18n.t("logistics.events.DELIVERY_ATTEMPTED"),
    };

    return translations[eventType] || eventType;
  },

  /**
   * Obter provedores de rastreamento disponíveis
   */
  getTrackingProviders() {
    return [
      { value: 'NAPP', label: 'NAPP Solutions' },
      { value: 'CORREIOS', label: 'Correios' },
      { value: 'LOGGI', label: 'Loggi' },
      { value: 'JADLOG', label: 'Jadlog' },
      { value: 'TOTAL_EXPRESS', label: 'Total Express' },
      { value: 'AZUL_CARGO', label: 'Azul Cargo' },
      { value: 'MERCADO_ENVIOS', label: 'Mercado Envios' },
      { value: 'IFOOD', label: 'iFood Delivery' },
      { value: 'UBER_DIRECT', label: 'Uber Direct' },
      { value: 'OTHER', label: i18n.t("logistics.providers.other") },
    ];
  },

  /**
   * Validar código de rastreamento
   */
  validateTrackingCode(code, provider = 'OTHER') {
    if (!code || code.trim().length === 0) {
      return { valid: false, message: i18n.t("logistics.validation.codeRequired") };
    }

    // Validações específicas por provedor
    if (provider === 'CORREIOS') {
      const correiosPattern = /^[A-Z]{2}[0-9]{9}[A-Z]{2}$/;
      if (!correiosPattern.test(code)) {
        return {
          valid: false,
          message: i18n.t("logistics.validation.correiosFormat")
        };
      }
    }

    if (code.length < 3 || code.length > 50) {
      return {
        valid: false,
        message: i18n.t("logistics.validation.codeLength")
      };
    }

    return { valid: true, message: '' };
  }
};

export default logisticsService;